import { Controller } from '@hotwired/stimulus'
import { get } from '@rails/request.js'

/**
 * Reports on Calls/Charges are triggered by ExportReportButtonController
 *
 * When visiting the /reports datatable it will then show a download button that may still be
 * in progress. This controller will poll the report endpoint to check export progress and
 * and update the button text and spinner.
 *
 * There's cross-over in functionality with ExportReportButtonController, but this button
 * is different enough where DRYing would be overkill.
 *
 * Usage:
 *   <button
 *     data-controller="export-report-in-progress-button"
 *     data-export-report-in-progress-button-poll-url-value="/reports/calls/123"
 *     data-export-report-in-progress-button-poll-url-attribute-value="file_upload_url">
 *     Download CSV
 *   </button>
 *
 * For example:
 *  In custom reports for calls it runs a background job and marks it as download_in_progress
 *  This stimulus controller will poll /reports/calls/123
 *  When it completes
 *   app/models/report/concerns/exportable.rb exposes `file_upload_url`
 *   ^ This is returned in the JSON response
 *  We pass this as the pollUrl value, which we make the button's href and download attribute
 */
export default class extends Controller {
    static values = {
        pollUrl: String,
        pollUrlAttribute: { type: String, default: 'file_upload_url' },
    }

    static targets = ['text', 'spinner']

    connect() {
        this.pollingTimeout = null
        this.originalButtonText = this.element.textContent || ''

        // Start polling immediately
        this.startPolling()
    }

    disconnect() {
        // Clean up polling timeout when controller is disconnected
        this.stopPolling()
    }

    startPolling() {
        // Disable button and show initial "Exporting..." state
        this.element.disabled = true
        this.updateButtonText()

        // Begin the polling cycle
        this.poll()
    }

    stopPolling() {
        if (this.pollingTimeout) {
            clearTimeout(this.pollingTimeout)
            this.pollingTimeout = null
        }
    }

    async poll() {
        try {
            const response = await get(`${this.pollUrlValue}.json`, {
                responseKind: 'json',
            })

            if (!response.ok) {
                this.textTarget.textContent = 'Error'
                const error = new Error('Custom Report Export Failed')
                if (newrelic) newrelic.noticeError(error, { response })
                return
            }

            const data = await response.json

            // Check if export is still in progress
            if (data.in_progress) {
                // Update button with progress percentage if available
                this.updateButtonText(data.export_percentage)

                // Continue polling after 1 second
                this.pollingTimeout = setTimeout(() => this.poll(), 1000)
            } else {
                // Export is complete, get the download URL
                const downloadUrl = data[this.pollUrlAttributeValue]

                if (downloadUrl) {
                    this.replaceWithDownloadLink(downloadUrl)
                } else {
                    console.error(
                        `CustomReportsDownloadLinkPoll: ${this.pollUrlAttributeValue} not found in response`,
                    )
                    this.markUnavailable()
                }
            }
        } catch (error) {
            console.error('CustomReportsDownloadLinkPoll: fetch error', error)
            if (newrelic) newrelic.noticeError(err)
            this.textTarget.textContent = 'Error'
        }
    }

    updateButtonText(percentage) {
        if (!this.element) return

        const progressNumber = Number(percentage)
        const text =
            Number.isFinite(progressNumber) && progressNumber > 0
                ? `Exporting... (${Math.round(progressNumber)}%)`
                : 'Exporting...'

        // Update text target if available, otherwise update entire element
        if (this.hasTextTarget) {
            this.textTarget.textContent = text
        } else {
            this.element.textContent = text
        }
    }

    replaceWithDownloadLink(downloadUrl) {
        // Stop any ongoing polling
        this.stopPolling()

        // Update the existing element to become a download link
        this.element.href = downloadUrl
        this.element.target = '_blank'
        this.element.title = 'Download'
        this.element.classList.remove('disabled', 'pe-none', 'btn-light')
        this.element.classList.add('btn-success')

        this.spinnerTarget.classList.add('d-none')
        this.textTarget.textContent = 'Download'
    }

    markUnavailable() {
        this.stopPolling()
        this.spinnerTarget.classList.add('d-none')
        this.textTarget.textContent = 'Unavailable'
    }
}
